/**
 * SPIN TENET — CFD Setup Preflight & Validation Engine (Gate 1 Foundation)
 *
 * Verifies study/scenario readiness against the G1 basic CFD setup criteria.
 *
 * G1 RULE:
 * Wind speed and wind direction must be explicitly supplied by the user.
 * Unset / null states strictly fail preflight with "Wind speed required" and "Wind direction required".
 *
 * AXIOM:
 * Never declare "Ready to run" in G1.
 * Authoritative solver connection is deferred to future gates.
 * The terminal passing state is strictly: "READY FOR ENGINE SPECIFICATION".
 */

import type { CfdSetup } from '@contracts/index';

export interface ValidationCheckItem {
  id: string;
  label: string;
  passed: boolean;
  missingMessage: string;
}

export interface CfdValidationResult {
  isReady: boolean;
  statusLabel: 'READY FOR ENGINE SPECIFICATION' | 'INCOMPLETE SETUP';
  checks: ValidationCheckItem[];
  missingCount: number;
}

export function validateCfdSetup(
  cfdSetup?: CfdSetup | null,
  geometryCount: number = 0
): CfdValidationResult {
  if (!cfdSetup) {
    return {
      isReady: false,
      statusLabel: 'INCOMPLETE SETUP',
      checks: [],
      missingCount: 8
    };
  }

  const { wind, domain, boundaryConditions } = cfdSetup;

  // Strict user-defined check: null or undefined strictly fails
  const isWindSpeedDefined =
    wind?.speed !== null &&
    wind?.speed !== undefined &&
    typeof wind.speed === 'number' &&
    Number.isFinite(wind.speed) &&
    wind.speed >= 0;

  const isWindDirectionDefined =
    wind?.direction !== null &&
    wind?.direction !== undefined &&
    typeof wind.direction === 'number' &&
    Number.isFinite(wind.direction);

  const isDomainDefined =
    typeof domain?.width === 'number' &&
    domain.width > 0 &&
    typeof domain?.length === 'number' &&
    domain.length > 0 &&
    typeof domain?.height === 'number' &&
    domain.height > 0;

  const isGeometryAvailable = geometryCount > 0;

  const isInletDefined = Boolean(boundaryConditions?.inlet);
  const isOutletDefined = Boolean(boundaryConditions?.outlet);
  const isGroundDefined = Boolean(boundaryConditions?.ground);
  const isBuildingDefined = Boolean(boundaryConditions?.buildings);

  const checks: ValidationCheckItem[] = [
    {
      id: 'wind-speed',
      label: 'Wind speed defined',
      passed: isWindSpeedDefined,
      missingMessage: 'Wind speed required'
    },
    {
      id: 'wind-direction',
      label: 'Wind direction defined',
      passed: isWindDirectionDefined,
      missingMessage: 'Wind direction required'
    },
    {
      id: 'domain',
      label: 'Computational domain defined',
      passed: isDomainDefined,
      missingMessage: 'Computational domain required (W, L, H > 0 m)'
    },
    {
      id: 'geometry',
      label: 'Geometry available',
      passed: isGeometryAvailable,
      missingMessage: 'No physical building geometry available'
    },
    {
      id: 'inlet',
      label: 'Inlet defined',
      passed: isInletDefined,
      missingMessage: 'Inlet boundary type required'
    },
    {
      id: 'outlet',
      label: 'Outlet defined',
      passed: isOutletDefined,
      missingMessage: 'Outlet boundary type required'
    },
    {
      id: 'ground',
      label: 'Ground boundary defined',
      passed: isGroundDefined,
      missingMessage: 'Ground boundary type required'
    },
    {
      id: 'buildings',
      label: 'Building boundary defined',
      passed: isBuildingDefined,
      missingMessage: 'Building boundary type required'
    }
  ];

  const missingCount = checks.filter((c) => !c.passed).length;
  const isReady = missingCount === 0;

  return {
    isReady,
    statusLabel: isReady ? 'READY FOR ENGINE SPECIFICATION' : 'INCOMPLETE SETUP',
    checks,
    missingCount
  };
}
